import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Users, Globe, MapPin, Lock, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import Amenities from '../components/Amenities';
import { getCategoryInfo, getProductsByCategory } from '../data/workspaceData';
import { Button } from '../components/ui/button';
import PricingCard from './ui/pricing-card';

// Icon mapping
const iconMap = {
  Building2,
  Users,
  Globe,
  MapPin,
  Lock,
  Clock
};

const CategoryPage = ({ categoryId }) => {
  const navigate = useNavigate();
  const categoryInfo = getCategoryInfo(categoryId);
  const products = getProductsByCategory(categoryId);

  // Image mapping for different categories and sections
  const imageMapping = {
    'office-space': {
      hero: 'IMG_6757.webp',
      keyFeatures: {
        hero: 'IMG_6760.webp',
        items: ['IMG_6810.webp', 'https://images.squarespace-cdn.com/content/v1/5877fcf8ebbd1a0e70bf993e/1625285912027-7HK6VJ5B56JGIJSHZPVK/day+offices_3.webp?format=1000w', 'IMG_6855.webp', 'https://venturexindia.com/wp-content/uploads/2023/10/AM_5320-1-1024x682.webp', 'https://imageio.forbes.com/specials-images/imageserve/65048e3772cfcb064a28ff0b/0x0.webp?format=.webp&crop=3380,1900,x268,y0,safe&height=900&width=1600&fit=bounds', 'IMG_6757.webp']
      },
      benefits: {
        hero: 'IMG_6844.webp',
        items: ['IMG_6845.webp', 'IMG_6846.webp', 'IMG_6847.webp', 'https://assets.entrepreneur.com/content/3x2/2000/20190405142450-Comcast-Webinar-May.jpeg']
      }
    },
    'coworking': {
      hero: 'IMG_6839.webp',
      keyFeatures: {
        hero: 'IMG_6840.webp',
        items: ['https://www.networkworld.com/wp-content/uploads/2025/05/970440-0-15691900-1747204741-group_discussion_around_a_table_in_an_office_workspace_collaboration_communication_planning_strategy_tactics_teamwork_by_gaudilab_shutterstock_1100733734_2400x1600-100890806-orig.jpg?quality=50&strip=all&w=1024', 'IMG_6757.webp', 'IMG_E6855.webp', 'https://imageio.forbes.com/specials-images/imageserve/65048e3772cfcb064a28ff0b/0x0.webp?format=.webp&crop=3380,1900,x268,y0,safe&height=900&width=1600&fit=bounds', 'IMG_E6807.webp']
      },
      benefits: {
        hero: 'IMG_6850.webp',
        items: ['IMG_6855.webp', 'https://www.servcorp.com/media/30616/business-growth.jpg', 'IMG_6945.webp', 'IMG_E6760.webp', 'IMG_E6806.webp']
      }
    },
    'meeting-rooms': {
      hero: 'IMG_6808.webp',
      keyFeatures: {
        hero: 'IMG_6809.webp',
        items: ['IMG_6810.webp', 'https://www.bondcollective.com/wp-content/uploads/2019/10/image1.png', 'IMG_E6809.webp', 'https://workset.eu/wp-content/uploads/2021/09/support-delivery-overview.webp', 'https://assets.bayut.com/content/Prime_Business_Centre_b5078e49df_768d82bf4e.webp', 'https://ezspaces.in/wp-content/uploads/2024/09/image0_0.webp']
      },
      benefits: {
        hero: 'IMG_6813.webp',
        items: ['IMG_E6855.webp', 'IMG_E6854.webp', 'https://t4.ftcdn.net/.jpg/02/81/89/73/360_F_281897358_3rj9ZBSZHo5s0L1ug7uuIHadSxh9Cc75.webp', 'NTLE0082.webp']
      }
    },
    'virtual-office': {
      hero: 'https://images.pexels.com/photos/290275/pexels-photo-290275.jpeg?cs=srgb&dl=pexels-pixabay-290275.webp&fm=.webp',
      keyFeatures: {
        hero: 'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80',
        items: [
          'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80',
          'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
          'https://cdn.autonomous.ai/production/ecm/250609/Full-Guide-for-an-Ideal-Standing-Desk-Setup-in-2021_517b0adbfe3(1).webp',
          'https://images.unsplash.com/photo-1604328698692-f76ea9498e76?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
        ]
      },
      benefits: {
        hero: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80',
        items: [
          'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
          'https://cdn.autonomous.ai/production/ecm/250609/Full-Guide-for-an-Ideal-Standing-Desk-Setup-in-2021_517b0adbfe3(1).webp',
          'https://www.indiafilings.com/learn/wp-content/uploads/2018/12/Company-Registration-Offices-and-Fees-Rules-2014.webp',
          'https://images.unsplash.com/photo-1524758631624-e2822e304c36?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
        ]
      }
    },
    'business-addresses': {
      hero: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      keyFeatures: {
        hero: 'https://images.unsplash.com/photo-1560472355-536de3962603?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2026&q=80',
        items: [
          'https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80',
          'https://cdn.autonomous.ai/production/ecm/250609/Full-Guide-for-an-Ideal-Standing-Desk-Setup-in-2021_517b0adbfe3(1).webp',
          'https://images.unsplash.com/photo-1604328698692-f76ea9498e76?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
          'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1932&q=80'
        ]
      },
      benefits: {
        hero: 'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80',
        items: [
          'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
          'https://cdn.autonomous.ai/production/ecm/250609/Full-Guide-for-an-Ideal-Standing-Desk-Setup-in-2021_517b0adbfe3(1).webp',
          'https://images.pexels.com/photos/290275/pexels-photo-290275.jpeg?cs=srgb&dl=pexels-pixabay-290275.webp&fm=.webp',
          'https://www.indiafilings.com/learn/wp-content/uploads/2018/12/Company-Registration-Offices-and-Fees-Rules-2014.webp'
        ]
      }
    },
    'day-offices': {
      hero: 'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80',
      keyFeatures: {
        hero: 'https://images.pexels.com/photos/290275/pexels-photo-290275.jpeg?cs=srgb&dl=pexels-pixabay-290275.webp&fm=.webp',
        items: [
          'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80',
          'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMJA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
          'https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEgSRd4eiWmp4-dI5GmD2kDrpVlOfbjqQwJcVtxerQx2TbOCaB_mgePVtNtvMcAI1sBYyk3KIYEgK5c9RTFxzuagtsAoOEFgwmUpjMAjOixOy4BgWhmXdf6RQGmqgb-ttW7dmFl52JgXsKny/s1600/1512148994695.png',
          'https://images.unsplash.com/photo-1604328698692-f76ea9498e76?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
          'https://cdn.autonomous.ai/production/ecm/250609/Full-Guide-for-an-Ideal-Standing-Desk-Setup-in-2021_517b0adbfe3(1).webp'
        ]
      },
      benefits: {
        hero: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80',
        items: [
          'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
          'https://images.ctfassets.net/wp1lcwdav1p1/iLdIHCXKdfvrmcLFUDcjk/61422c7beb6f5922a9c6f66a2d0797aa/GettyImages-1276389782.jpeg?w=1500&h=680&q=60&fit=fill&f=faces&fm=.webp&fl=progressive',
          'https://www.indiafilings.com/learn/wp-content/uploads/2018/12/Company-Registration-Offices-and-Fees-Rules-2014.webp',
          'https://images.unsplash.com/photo-1524758631624-e2822e304c36?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
          'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1932&q=80',
          'https://www.indiafilings.com/learn/wp-content/uploads/2018/12/Company-Registration-Offices-and-Fees-Rules-2014.webp'
        ]
      }
    }
  };

  // Category-specific content
  const categoryContent = {
    'office-space': {
      heroTitle: 'Inspiring Office Space',
      heroSubtitle: 'Designed for Business Growth',
      mainDescription: 'At Laksh Space, our fully furnished private office spaces are designed to empower professionals, entrepreneurs, and teams with the privacy, comfort, and flexibility they need to perform at their best. Whether you\'re looking for a single-seater cabin, a team cabin for 2 or 6 members, or a day office for short-term use, we provide premium workspaces that reflect professionalism and enhance productivity.',
      secondaryDescription: 'Each office is designed to help your business operate efficiently, while our on-site team ensures everything runs seamlessly — from setup to daily support.',
      keyFeatures: {
        title: 'Key Features of Our Office Spaces',
        items: [
          {
            title: 'Private Cabins (1, 2 & 6 Seater)',
            description: 'Experience complete privacy in our ergonomically designed cabins, perfect for individuals or teams who prefer a focused and professional environment.'
          },
          {
            title: 'Day Offices',
            description: 'Access fully equipped offices for a day — ideal for business travelers, remote workers, or professionals needing a workspace on demand.'
          },
          {
            title: 'Meeting Rooms',
            description: 'Host clients or team discussions in our well-appointed meeting spaces equipped with modern technology and amenities.'
          },
          {
            title: 'Premium Business Address',
            description: 'Establish a credible presence in a prime Bangalore location that enhances your brand image and accessibility.'
          },
          {
            title: 'High-Speed Internet & IT Support',
            description: 'Stay connected with business-grade internet and on-site IT assistance to keep your operations running smoothly.'
          },
          {
            title: 'Reception & Administrative Support',
            description: 'Our professional front desk team welcomes your guests and provides essential administrative assistance throughout the day.'
          }
        ]
      },
      benefits: {
        title: 'Benefits of Choosing Laksh Space Office Solutions',
        description: 'Our private office spaces are designed to support every stage of your business journey, from solo entrepreneurs to established companies. Each office solution provides the perfect balance of privacy, professionalism, and flexibility, ensuring your workspace adapts to your evolving business needs while maintaining the highest standards of comfort and functionality.',
        items: [
          {
            title: 'For Individuals & Freelancers',
            description: 'Enjoy a professional environment that boosts concentration and productivity, with flexible options that suit your workflow and schedule. Our individual office solutions provide the perfect escape from home office distractions while establishing a credible business presence that impresses clients and partners. ✅ Private, distraction-free workspace designed for maximum focus • ✅ Professional business address for credibility • ✅ Flexible lease terms that adapt to your growing business • ✅ Access to meeting rooms for client interactions • ✅ Networking opportunities with other professionals • ✅ All-inclusive pricing with no hidden operational costs'
          },
          {
            title: 'For Teams & Growing Businesses',
            description: 'Operate from private, secure spaces that encourage collaboration while maintaining confidentiality and comfort. Our team offices are designed to foster productivity and teamwork, with flexible configurations that can adapt as your business grows. Perfect for startups, established SMEs, and project-based teams. ✅ Scalable office solutions that grow with your team • ✅ Private spaces for confidential discussions and strategic planning • ✅ Collaborative areas that encourage innovation and teamwork • ✅ Professional environment that attracts and retains talent • ✅ Comprehensive business services and administrative support • ✅ Secure, access-controlled premises for peace of mind'
          },
          {
            title: 'For Short-Term & Project-Based Work',
            description: 'Our flexible office solutions allow you to work efficiently without long-term commitments — perfect for project teams, temporary assignments, or business expansion testing. Whether you need space for a few days or several months, we provide fully-equipped offices ready for immediate occupation. ✅ No long-term contracts or commitments required • ✅ Immediate availability with advance booking • ✅ Fully-furnished spaces with all necessary amenities • ✅ Professional business services included • ✅ Easy scaling up or down based on project requirements • ✅ Cost-effective alternative to traditional office leasing'
          },
          {
            title: 'For Established Companies & Long-Term Growth',
            description: 'Benefit from stable, fully serviced workspace solutions that evolve with your business needs, backed by a dedicated support team and comprehensive business services. Our long-term office solutions provide the foundation for sustainable growth while maintaining operational flexibility. ✅ Dedicated account management and personalized service • ✅ Customizable office layouts and branding opportunities • ✅ Access to additional services as your business expands • ✅ Priority booking for meeting rooms and event spaces • ✅ Comprehensive business support services • ✅ Strategic location that enhances your business presence'
          },
          {
            title: 'For Business Credibility & Professional Image',
            description: 'Establish and maintain a professional business image with premium office spaces in prestigious locations. Our offices provide the credibility and sophistication that clients, partners, and employees expect from successful businesses. Perfect for companies looking to make a strong first impression and build lasting business relationships. ✅ Prestigious business address that enhances company image • ✅ Professional reception and guest services • ✅ High-quality office furnishings and modern amenities • ✅ Access to executive meeting rooms and conference facilities • ✅ Professional administrative support when needed • ✅ Secure, well-maintained business environment'
          },
          {
            title: 'For Operational Efficiency & Cost Management',
            description: 'Streamline your business operations with all-inclusive office solutions that eliminate the complexity and cost of managing traditional office space. Focus on your core business activities while we handle facility management, maintenance, and support services. ✅ All-inclusive pricing covers utilities, maintenance, and services • ✅ No capital investment required for office setup • ✅ Professional IT support and high-speed connectivity • ✅ Comprehensive facility management and cleaning services • ✅ Flexible terms that adapt to changing business needs • ✅ Transparent pricing with no unexpected costs or fees'
          }
        ]
      }
    },
    'coworking': {
      heroTitle: 'We Take Care of Your Workspace',
      heroSubtitle: 'You Focus on Success',
      mainDescription: 'Welcome to Laksh Space, your destination for premium coworking spaces in Bangalore — designed for freelancers, startups, and growing businesses. Our modern coworking environment offers the perfect mix of privacy, productivity, and collaboration. Whether you prefer to hot desk for a day or rent a dedicated desk long-term, every workspace comes fully serviced and ready to use.',
      secondaryDescription: 'With high-speed internet, ergonomic furniture, and professional on-site support, you can focus on your goals while we take care of the rest. Experience the perfect balance of community, convenience, and professionalism — all in one premium location.',
      keyFeatures: {
        title: 'Key Features of Our Coworking Space',
        items: [
          {
            title: 'Network & Collaboration',
            description: 'Connect and collaborate with like-minded professionals in a vibrant, inspiring community — or work quietly in a well-designed shared office space.'
          },
          {
            title: 'Reception Services',
            description: 'Our professional reception team is available to assist you and warmly welcome your guests, creating a seamless business experience.'
          },
          {
            title: 'Meeting Rooms',
            description: 'Book fully equipped meeting rooms for presentations, client meetings, or brainstorming sessions — all within the same facility.'
          },
          {
            title: 'High-Speed Wi-Fi & IT Support',
            description: 'Stay connected with business-grade internet, shared printing facilities, and complete on-site IT assistance to keep your workflow uninterrupted.'
          },
          {
            title: 'Premium Lounges',
            description: 'Relax or network in our elegant business lounges, designed to provide comfort and a professional ambiance throughout your workday.'
          }
        ]
      },
      benefits: {
        title: 'Coworking Benefits for Every Business',
        description: 'At Laksh Space Bangalore, we cater to professionals and businesses of every size — from individual entrepreneurs and freelancers to small and mid-sized teams. Our coworking offices in Bangalore offer flexible terms, affordable pricing, and full operational support, including reception, IT, cleaning, and security services.',
        items: [
          {
            title: 'For Startups & Freelancers',
            description: 'Experience cost-effective coworking solutions that combine focus, flexibility, and networking opportunities to help your business grow.'
          },
          {
            title: 'For Teams & Growing Businesses',
            description: 'Choose from dedicated desks or private cabins tailored to your team\'s size and needs, with access to all premium amenities in a professional environment that fosters productivity and collaboration.'
          }
        ],
        growthSection: {
          title: 'Coworking Can Help Grow Your Business',
          description: 'At Laksh Space, our coworking environments bring together a diverse community of professionals, startups, and established companies — creating endless opportunities for collaboration and growth. When you choose our coworking space, you don\'t just rent a desk — you become part of a professional network that inspires innovation and business connections. Engage with like-minded individuals, attend curated networking events, and build valuable relationships that help accelerate your success.'
        },
        additionalFeatures: [
          {
            title: 'Professional Business Address',
            description: 'Establish your presence in a reputable business location that strengthens your brand image and credibility.'
          },
          {
            title: 'Networking & Events',
            description: 'Gain access to regular business and personal development events designed to foster collaboration, partnerships, and growth.'
          },
          {
            title: 'Strategic Location',
            description: 'Work from a prime Bangalore location that keeps you connected to your industry and key business hubs.'
          },
          {
            title: 'Collaborative Breakout Areas',
            description: 'Connect, collaborate, and exchange ideas in our thoughtfully designed breakout zones that encourage interaction and creativity.'
          }
        ]
      }
    },
    'meeting-rooms': {
      heroTitle: 'Empower Your Business with',
      heroSubtitle: 'Premium Meeting Spaces',
      mainDescription: 'At Laksh Space, our state-of-the-art Meeting Rooms in Bangalore provide the ideal environment for professional discussions, client presentations, interviews, and team collaborations. Choose from our 4-seater, 6-seater, or 8-seater meeting rooms, each designed with comfort, privacy, and functionality in mind.',
      secondaryDescription: 'Whether it\'s a strategy session, board meeting, or client conference, our premium meeting spaces help you make a strong impression and drive results.',
      keyFeatures: {
        title: 'Key Features of Our Meeting Rooms',
        items: [
          {
            title: 'Flexible Room Options',
            description: 'Choose from 4, 6, or 8-seater rooms tailored to your business requirements — perfect for meetings, conferences, or brainstorming sessions.'
          },
          {
            title: 'Modern Amenities',
            description: 'Each room is equipped with high-speed internet, presentation screens, whiteboards, and video conferencing facilities for a seamless meeting experience.'
          },
          {
            title: 'Professional Ambiance',
            description: 'Enjoy a quiet, well-lit, and fully air-conditioned setup designed to enhance focus, comfort, and professionalism.'
          },
          {
            title: 'On-Site Support',
            description: 'Our reception and operations team ensure every meeting runs smoothly — from greeting guests to handling technical arrangements.'
          },
          {
            title: 'Prime Business Location',
            description: 'Host meetings at a prestigious business address in Bangalore that reflects credibility and convenience for your clients and partners.'
          },
          {
            title: 'Flexible Booking Options',
            description: 'Book meeting rooms by the hour or day — ideal for both members and non-members who need flexible access to professional spaces.'
          }
        ]
      },
      benefits: {
        title: 'Benefits of Choosing Laksh Space Meeting Rooms',
        description: 'Our meeting rooms are designed to support every type of business interaction, from intimate client consultations to large team collaborations. Each space is carefully crafted to enhance communication, productivity, and professional impression while providing the flexibility and technology support modern businesses demand.',
        items: [
          {
            title: 'For Professionals & Entrepreneurs',
            description: 'Create the right impression with clients, conduct interviews, or hold presentations in a premium, distraction-free environment. Our meeting rooms provide the professional setting that establishes credibility and trust from the first interaction. Perfect for client meetings, investor pitches, partnership discussions, and important negotiations. ✅ Professional ambiance that impresses clients • ✅ State-of-the-art presentation technology • ✅ Comfortable seating arrangements • ✅ Quiet, private environment for confidential discussions • ✅ Professional reception services for guest welcome'
          },
          {
            title: 'For Startups & Teams',
            description: 'Collaborate effectively in private, fully serviced rooms ideal for planning, brainstorming, and performance discussions. Our flexible meeting spaces adapt to your team\'s working style and provide the collaborative environment necessary for innovation and growth. Whether it\'s strategy sessions, product development meetings, or team building activities, we provide the perfect setting. ✅ Flexible seating arrangements for different meeting types • ✅ Whiteboards and brainstorming tools • ✅ High-speed internet for seamless collaboration • ✅ Privacy for confidential strategic discussions • ✅ Extended booking options for intensive work sessions'
          },
          {
            title: 'For Corporate Use',
            description: 'Host board meetings, vendor reviews, or virtual conferences with reliable connectivity and dedicated on-site support. Our corporate-grade meeting rooms provide the sophistication and functionality required for high-level business operations. Impress stakeholders and ensure smooth operations with professional facilities and comprehensive technical support. ✅ Executive-level ambiance and comfort • ✅ Advanced video conferencing capabilities • ✅ Dedicated technical support during meetings • ✅ Catering services available for extended sessions • ✅ Secure environment for sensitive business discussions • ✅ Professional documentation and recording facilities'
          },
          {
            title: 'For Training & Workshops',
            description: 'Utilize spacious, tech-enabled rooms for workshops, team training, or product demonstrations in a professional setting. Our meeting rooms are designed to facilitate learning and engagement, with flexible layouts and comprehensive technology support. Perfect for skills development, product launches, educational seminars, and interactive workshops. ✅ Flexible room layouts to accommodate different training styles • ✅ Professional presentation equipment and screens • ✅ Interactive whiteboards and collaboration tools • ✅ Comfortable seating for extended learning sessions • ✅ Break areas and refreshment facilities • ✅ Technical support for multimedia presentations'
          },
          {
            title: 'For Client Presentations & Sales Meetings',
            description: 'Deliver compelling presentations and close important deals in rooms designed to support your sales process. Our meeting rooms provide the professional backdrop that reinforces your company\'s credibility while offering the technology and comfort needed for successful client interactions. Make every presentation count with our premium facilities. ✅ Professional presentation setup with multiple screens • ✅ Impressive business environment that builds trust • ✅ Comfortable client seating and hospitality • ✅ Reliable technology that never fails during crucial moments • ✅ Privacy for sensitive negotiations and discussions • ✅ Follow-up facilities including private phone and internet access'
          },
          {
            title: 'For Virtual & Hybrid Meetings',
            description: 'Seamlessly connect remote participants with in-person attendees using our advanced video conferencing facilities. Our meeting rooms are optimized for hybrid meetings, ensuring all participants can engage effectively regardless of their location. Perfect for global teams, remote interviews, and distributed project collaborations. ✅ Professional video conferencing setup with multiple cameras • ✅ High-quality audio systems for clear communication • ✅ Screen sharing capabilities for seamless presentations • ✅ Stable, high-speed internet connectivity • ✅ Technical support for troubleshooting during calls • ✅ Recording capabilities for meeting documentation'
          }
        ]
      }
    },
    'virtual-office': {
      heroTitle: 'Virtual Office',
      heroSubtitle: 'Enhance Your Business Identity with Laksh Space Virtual Office',
      mainDescription: 'A virtual office provides your business an address at any of our office locations worldwide, enabling you to build an instant presence, without the costs of a dedicated office. We can handle your mail and forward it on, take your calls, and you can easily book a meeting room or office when you\'re in town.',
      secondaryDescription: 'At Laksh Space, we understand that not every business requires a full-time physical office. Our Virtual Office solutions help you establish a professional presence with a premium business address — without the cost or commitment of traditional office space. Whether you\'re a startup, freelancer, or growing company, a virtual office gives you the flexibility to operate from anywhere while maintaining a credible business image. Access meeting spaces and office facilities whenever needed — all managed seamlessly by our team.',
      keyFeatures: {
        title: 'Choose Your Virtual Office Plan',
        description: 'Select the plan that best fits your business needs and budget. All plans include professional business address services with flexible terms and transparent pricing.',
        items: [
          {
            title: 'Business Address Plan',
            description: '₹ 4,999/ per year + GST (₹ 3,999/ per year + GST when you sign up for 2 years) ✅ Ideal for: Basic business address needs ✅ Courier Receiving and Forwarding Services ✅ Use Professional Address Across All Platforms Effectively ✅ Access Meeting Rooms as Per Requirements ✅ No Hidden Charges'
          },
          {
            title: 'GST Registration Plan',
            description: '₹ 5,999/ per year + GST (₹ 4,999/ per year + GST when you sign up for 2 years) ✅ Ideal for: GST registration and business operations ✅ Update Your Registered Address Officially ✅ Use the Address for Opening a Bank Account ✅ Use Address for GST ✅ On-site GST Verification ✅ Courier Receiving and Forwarding Services ✅ Use Professional Address Across All Platforms Effectively ✅ Access Meeting Rooms as Per Requirements ✅ No Hidden Charges'
          },
          {
            title: 'Company Registration Plan',
            description: '₹ 7,999/ per year + GST (₹ 6,999/ per year + GST when you sign up for 2 years) ✅ Ideal for: New Company, New Address, etc. ✅ Update Your Registered Address Officially ✅ Use the Address for Opening a Bank Account ✅ Use Address for GST Certification ✅ On-site GST Verification ✅ Courier Receiving and Forwarding Services ✅ Use Professional Address Across All Platforms Effectively ✅ Access Meeting Rooms as Per Requirements ✅ No Hidden Charges'
          },
          {
            title: 'Premium Virtual Office Services',
            description: 'Enhance your virtual office experience with additional services including call handling, professional receptionist support, document management, and priority access to meeting rooms and coworking spaces whenever you need them.'
          }
        ]
      },
      benefits: {
        title: 'Why Choose Laksh Space Virtual Office Solutions',
        description: 'Our virtual office plans are designed to provide maximum flexibility and professional credibility while keeping costs manageable. Each plan includes comprehensive business support services that help establish and maintain your professional presence.',
        items: [
          {
            title: 'For Startups & New Businesses',
            description: 'Establish instant credibility with a professional business address that enhances your brand image from day one. Perfect for new companies needing official registration addresses and professional communication channels. ✅ Verified business address for company registration ✅ GST registration support and verification ✅ Professional business image and credibility ✅ Cost-effective alternative to physical office space ✅ Flexible terms that grow with your business ✅ Access to meeting rooms for client interactions'
          },
          {
            title: 'For Freelancers & Remote Workers',
            description: 'Separate your personal and professional identity with a dedicated business address that protects your privacy while establishing professional credibility. Ideal for freelancers, consultants, and remote professionals who need a business presence. ✅ Professional business address for all communications ✅ Mail handling and forwarding services ✅ Privacy protection for home address ✅ Access to professional meeting spaces ✅ Enhanced business credibility with clients ✅ Flexible access to coworking facilities'
          },
          {
            title: 'For Growing Companies',
            description: 'Scale your business presence without the overhead of traditional office leasing. Our virtual office solutions provide the flexibility to expand into new markets and establish regional presence cost-effectively. ✅ Multiple location presence without physical offices ✅ Professional address in prime business district ✅ Scalable solutions as your business grows ✅ Access to meeting rooms and office space on demand ✅ Professional administrative support services ✅ Cost-effective expansion into new markets'
          },
          {
            title: 'For Business Registration & Compliance',
            description: 'Ensure full compliance with business registration requirements using our verified business addresses. Perfect for company incorporation, GST registration, and maintaining official business records. ✅ Verified address for company registration ✅ GST registration and compliance support ✅ On-site verification services available ✅ Bank account opening support ✅ Official documentation assistance ✅ Compliance with regulatory requirements'
          },
          {
            title: 'For Professional Image & Credibility',
            description: 'Project a professional image that builds trust with clients, partners, and stakeholders. Our premium business addresses enhance your company\'s credibility and market positioning. ✅ Premium business address in prestigious location ✅ Professional mail handling and forwarding ✅ Enhanced business credibility and trust ✅ Professional phone and communication services ✅ Access to executive meeting facilities ✅ Consistent professional brand presence'
          },
          {
            title: 'For Operational Efficiency',
            description: 'Streamline your business operations with comprehensive virtual office services that handle administrative tasks, allowing you to focus on core business activities. ✅ Complete mail and courier management ✅ Professional call handling services ✅ Administrative support when needed ✅ Meeting room booking and management ✅ Document handling and storage ✅ Transparent pricing with no hidden costs'
          }
        ]
      }
    },
    'business-addresses': {
      heroTitle: 'Business Address',
      heroSubtitle: 'Build Your Company\'s Professional Presence',
      mainDescription: 'Build your company\'s presence with a prestigious business address — without the cost or commitment of a physical office. With Laksh Space\'s Business Address Plan, you can project a professional image, protect your privacy, and use our verified address for company or GST registration (where permitted).',
      secondaryDescription: 'Our team manages your mail handling and forwarding, and you\'ll always have the flexibility to book meeting rooms or office space whenever you need them. Our virtual office solutions offer more than just an address — find the plan that fits your business best.',
      keyFeatures: {
        title: 'Choose Your Business Address Solution',
        description: 'Select from our comprehensive range of business address and virtual office plans designed to meet different business needs and growth stages.',
        items: [
          {
            title: 'Business Address Plan - Create a Professional Presence',
            description: 'Get a verified business address in a premium location for company or GST registration, branding, and client communication. Includes mail handling and forwarding services for a seamless experience.'
          },
          {
            title: 'Virtual Office Plan - Work Smarter, Stay Connected',
            description: 'Combine the benefits of a professional address with access to our coworking and meeting spaces. Ideal for entrepreneurs and growing teams who need flexibility without the cost of a full-time office.'
          },
          {
            title: 'Virtual Office Plus - A Complete Business Solution',
            description: 'Everything in the Virtual Office Plan, plus priority access to meeting rooms and private cabins for up to 5 days a month — giving you the perfect balance between virtual flexibility and physical presence.'
          },
          {
            title: 'Business Support Add-ons - Professional Assistance When You Need It',
            description: 'Get access to on-demand workspace, documentation support, and client hosting facilities, all designed to help you focus on your business while we handle the rest.'
          }
        ]
      },
      benefits: {
        title: 'A Prime Office Address to Build Your Business',
        description: 'Our Business Address Plan gives your company a prestigious office address in a key business location, helping you create a strong professional presence without the expense of a dedicated office. Ideal for startups, freelancers, and growing businesses, it\'s a perfect solution for building credibility and managing official communication. You can also use the address for company or GST registration, as per local guidelines.',
        items: [
          {
            title: 'A Business Address Is Just the Start',
            description: 'At Laksh Space, your business address is more than a location — it\'s the foundation of your professional identity. Beyond providing a credible address, our Business Address Plan comes with added advantages designed to help your business grow. Here\'s what\'s included: ✅ A verified business address for your website, company, or GST registration ✅ Professional on-site support from our dedicated team ✅ Access to community networking and business events ✅ Front desk team to welcome your clients and visitors ✅ Reliable mail handling and forwarding services (with add-on options) ✅ Access to coworking desks and meeting rooms on demand'
          },
          {
            title: 'For Startups & New Companies',
            description: 'Establish instant business credibility with a professional address that enhances your company image from day one. Perfect for new businesses needing official registration addresses and professional communication channels without the overhead of physical office space. ✅ Verified business address for company registration ✅ Professional business image and credibility ✅ Cost-effective alternative to physical office space ✅ Flexible terms that grow with your business ✅ Access to meeting rooms for client interactions ✅ Mail handling and administrative support'
          },
          {
            title: 'For Remote Businesses & Freelancers',
            description: 'Separate your personal and professional identity with a dedicated business address that protects your privacy while establishing professional credibility. Ideal for remote workers, consultants, and home-based businesses. ✅ Professional business address for all communications ✅ Privacy protection for home address ✅ Enhanced business credibility with clients ✅ Mail handling and forwarding services ✅ Access to professional meeting spaces when needed ✅ Professional reception services for visitors'
          },
          {
            title: 'For Business Registration & Compliance',
            description: 'Ensure full compliance with business registration requirements using our verified business addresses. Perfect for company incorporation, GST registration, and maintaining official business records with regulatory authorities. ✅ Verified address for company registration ✅ GST registration support and compliance ✅ Bank account opening assistance ✅ Official documentation support ✅ Compliance with regulatory requirements ✅ Professional administrative assistance'
          },
          {
            title: 'For Professional Image & Growth',
            description: 'Project a professional image that builds trust with clients, partners, and stakeholders. Our premium business addresses enhance your company\'s credibility and market positioning while providing the flexibility to scale. ✅ Premium business address in prestigious location ✅ Professional mail handling and forwarding ✅ Enhanced business credibility and trust ✅ Access to professional meeting facilities ✅ Networking opportunities with other businesses ✅ Scalable solutions as your business grows'
          },
          {
            title: 'For Cost-Effective Operations',
            description: 'Streamline your business operations with comprehensive business address services that eliminate the complexity and cost of managing traditional office space while maintaining professional standards. ✅ All-inclusive business address services ✅ No capital investment required for office setup ✅ Professional administrative support when needed ✅ Flexible access to meeting rooms and facilities ✅ Transparent pricing with no hidden costs ✅ Complete mail and courier management services'
          }
        ]
      }
    },
    'day-offices': {
      heroTitle: 'Day Offices',
      heroSubtitle: 'Professional Workspace by the Hour or Day',
      mainDescription: 'Need a professional space for a day? Our Day Offices offer fully-equipped private cabins designed for focus and productivity. Enjoy a flexible, hassle-free workspace with high-speed Wi-Fi, comfortable furniture, and a peaceful environment — perfect for client meetings, remote work, or short-term projects.',
      secondaryDescription: 'Whether you need it for a few hours or a full day, Laksh Space provides the ideal setup to help you work your best.',
      keyFeatures: {
        title: 'Rent an Office for a Day — No Long-Term Commitment',
        description: 'Experience the convenience of a fully-equipped private office for a day — perfect for meetings, focused work, or short-term projects. Get all the benefits of a professional workspace without the need for a full-time rental.',
        items: [
          {
            title: 'What\'s Included in Your Day Office',
            description: 'Our day offices come fully equipped with everything you need for a productive workday. From modern technology to comfortable furnishings, we\'ve got you covered. ✅ Modern, ready-to-use private cabins with business-grade Wi-Fi ✅ Flexible booking — by the hour or for the full day ✅ Access to meeting rooms and common areas ✅ Professional workspace that enhances your business image ✅ On-site support to ensure a smooth work experience'
          },
          {
            title: 'Daily or Hourly Office Space — Everything Included',
            description: 'At Laksh Space, enjoy the flexibility to rent an office by the hour or by the day — perfect for meetings, focused work, or short-term projects. Our ready-to-use offices come fully equipped with all essentials, so you can simply walk in and get to work. ✅ Professional on-site team to assist and support your business needs ✅ High-speed Wi-Fi with complete tech support ✅ Ergonomic furniture and access to shared printers ✅ All-inclusive pricing — cleaning, electricity, and security covered'
          },
          {
            title: 'Professional Business Environment',
            description: 'Create the right impression with clients and colleagues in our premium office spaces designed for productivity and professionalism. Every detail is crafted to enhance your business image and ensure maximum comfort during your workday.'
          },
          {
            title: 'Flexible Terms & Transparent Pricing',
            description: 'No long-term commitments required. Book by the hour or full day with transparent, all-inclusive pricing that covers everything you need for a successful work session.'
          }
        ]
      },
      benefits: {
        title: 'Why a Day Office Could Be Right for You',
        description: 'A Day Office is the smart choice when you need a professional workspace without the long-term cost or commitment. It\'s flexible, affordable, and designed to fit the way you work.',
        items: [
          {
            title: 'Perfect For Startups',
            description: 'A Day Office gives you all the benefits of a professional workspace — without the long-term commitment or high costs. It\'s flexible, private, and perfect for when you just need a space to get things done. Ideal for startups — a budget-friendly way to work professionally. ✅ Cost-effective workspace solution ✅ Professional business environment ✅ Flexible booking options ✅ No long-term commitments ✅ Access to meeting facilities ✅ Professional business address'
          },
          {
            title: 'Ideal for Freelancers',
            description: 'Freelancers who occasionally need to meet clients in a professional setting will find our day offices perfect for building credibility and maintaining professional relationships. ✅ Private setup to meet or work near clients ✅ Professional environment that impresses clients ✅ Flexible hourly and daily booking ✅ Access to presentation facilities ✅ Professional business support ✅ Quiet, distraction-free workspace'
          },
          {
            title: 'Perfect for Remote Professionals',
            description: 'Remote professionals seeking a distraction-free environment to focus will appreciate our quiet, well-equipped offices designed for maximum productivity. ✅ Distraction-free space away from home ✅ Professional work environment ✅ High-speed internet and tech support ✅ Comfortable, ergonomic workspace ✅ Professional atmosphere that enhances focus ✅ Access to business facilities'
          },
          {
            title: 'Great for Project Teams',
            description: 'Teams that require a short-term office for specific projects can utilize our collaborative spaces designed to facilitate teamwork and innovation. ✅ Temporary office space for short-term work ✅ Collaborative workspace layouts ✅ Meeting rooms for team discussions ✅ Professional project environment ✅ Flexible space configurations ✅ All necessary business amenities'
          },
          {
            title: 'Why a Day Office Might Be Right for You',
            description: 'A Day Office gives you all the benefits of a professional workspace — without the long-term commitment or high costs. It\'s flexible, private, and perfect for when you just need a space to get things done. Experience the convenience and professionalism that can transform your workday. ✅ Flexible, affordable workspace solution ✅ Professional environment without long-term costs ✅ Private offices designed for productivity ✅ Perfect for occasional professional space needs ✅ All-inclusive pricing with no hidden fees ✅ Immediate availability for your business needs'
          }
        ]
      }
    }
  };

  if (!categoryInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Category Not Found</h1>
          <Button onClick={() => navigate('/products')} className="bg-[#e30613] hover:bg-[#c1050f]">
            View All Products
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const IconComponent = iconMap[categoryInfo.icon] || Building2;
  const content = categoryContent[categoryId];

  // Helper function to get image source
  const getImageSrc = (imagePath) => {
    if (!imagePath) return './images-optimized/galery-images/IMG_6757.webp';
    
    // Check if it's already a URL (starts with http)
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // Otherwise, it's a local image
    return `./images-optimized/galery-images/${imagePath}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Back Navigation */}
          <button 
            onClick={() => navigate('/products')}
            className="flex items-center space-x-2 text-[#e30613] hover:text-[#c1050f] mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back to All Products</span>
          </button>

          {/* Category Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-[#e30613] rounded-2xl shadow-lg">
                <IconComponent className="w-12 h-12 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <h1 className="text-5xl lg:text-6xl font-black text-gray-900 mb-4 leading-tight">
              {content?.heroTitle || categoryInfo.title}
            </h1>
            <h2 className="text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#e30613] to-[#c1050f] mb-6">
              {content?.heroSubtitle}
            </h2>
            <div className="max-w-4xl mx-auto space-y-4">
              <p className="text-xl text-gray-700 leading-relaxed">
                {content?.mainDescription}
              </p>
              {content?.secondaryDescription && (
                <p className="text-lg text-gray-600 leading-relaxed">
                  {content.secondaryDescription}
                </p>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-600 bg-white rounded-lg p-4 shadow-sm">
            <span className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span className="font-medium">{products.length} options available</span>
            </span>
            <span>Professional workspace solutions</span>
            <span>Flexible terms available</span>
            <span>Prime Bangalore location</span>
          </div>
        </div>
      </div>

      {/* Key Features Section - New Format */}
      {content?.keyFeatures && (
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            {/* Special Pricing Cards Layout for Virtual Office */}
            {categoryId === 'virtual-office' ? (
              <div>
                {/* Title */}
                <div className="text-center mb-16">
                  <h2 className="text-3xl lg:text-4xl font-bold text-[#e30613] mb-6">
                    {content.keyFeatures.title}
                  </h2>
                  {content.keyFeatures.description && (
                    <p className="text-gray-700 text-lg leading-relaxed max-w-4xl mx-auto">
                      {content.keyFeatures.description}
                    </p>
                  )}
                </div>

                {/* Pricing Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                  {/* Business Address Plan */}
                  <PricingCard 
                    icon={
                      <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2L3 7v11h14V7l-7-5zM8 16H6v-4h2v4zm0-6H6V8h2v2zm4 6h-2v-4h2v4zm0-6h-2V8h2v2zm4 6h-2v-4h2v4zm0-6h-2V8h2v2z"/>
                      </svg>
                    }
                    title="Business Address Plan"
                    price="₹ 9,500"
                    originalPrice="₹ 7,250"
                    period="per year + GST"
                    discountText="when you sign up for 2 years"
                    buttonText="Get Quote"
                    features={[
                      "For communication, branding, and marketing",
                      "Handling of mail and packages",
                      "Service Agreement outlining your Virtual Office terms and usage rights with BHIVE",
                      "Can be used for business cards, website, letterhead, mail communication"
                    ]}
                  />

                  {/* GST Registration Plan */}
                  <PricingCard 
                    icon={
                      <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    }
                    title="GST Registration Plan"
                    price="₹ 14,000"
                    originalPrice="₹ 10,750"
                    period="per year + GST"
                    discountText="when you sign up for 2 years"
                    buttonText="Get Quote"
                    features={[
                      "For GST registration",
                      "Handling of mail and packages",
                      "NOC from BHIVE and the landlord authorizing use of the address for your business",
                      "Board Resolution template from BHIVE",
                      "Rental Agreement to serve as official proof of business address",
                      "Can be used for GST application, ITC claims"
                    ]}
                  />

                  {/* Company Registration Plan */}
                  <PricingCard 
                    icon={
                      <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z M4 5a2 2 0 012-2v1a1 1 0 00-1 1v6a1 1 0 001 1v1a2 2 0 01-2-2V5zM16 5a2 2 0 00-2-2v1a1 1 0 011 1v6a1 1 0 01-1 1v1a2 2 0 002-2V5z"/>
                      </svg>
                    }
                    title="Company Registration Plan"
                    price="₹ 24,000"
                    originalPrice="₹ 18,000"
                    period="per year + GST"
                    discountText="when you sign up for 2 years"
                    buttonText="Get Quote"
                    features={[
                      "For ROC registration",
                      "Handling of mail and packages",
                      "NOC from BHIVE and the landlord authorizing use of the address for your business",
                      "Board Resolution template from BHIVE",
                      "Rental Agreement to serve as official proof of business address",
                      "Utility Bill to validate the address for legal and tax purposes",
                      "Can be used for company incorporation, ROC filings"
                    ]}
                  />
                </div>
              </div>
            ) : (
              /* Default Features Layout for Other Categories */
              <div>
                {/* Centered Title */}
                <div className="text-center mb-16">
                  <h2 className="text-3xl lg:text-4xl font-bold text-[#e30613] mb-6">
                    {content.keyFeatures.title}
                  </h2>
                  {content.keyFeatures.description && (
                    <p className="text-gray-700 text-lg leading-relaxed max-w-4xl mx-auto">
                      {content.keyFeatures.description}
                    </p>
                  )}
                </div>

                {/* Features List with Alternating Layout */}
                {content.keyFeatures.items.map((feature, index) => {
                  const isEven = index % 2 === 0;
                  const categoryImages = imageMapping[categoryId]?.keyFeatures?.items || ['IMG_6757.webp', 'IMG_6840.webp', 'IMG_6843.webp'];
                  const imageIndex = index % categoryImages.length;
                  
                  return (
                    <div key={index} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                      <div className={isEven ? 'order-2 lg:order-1' : 'order-2 lg:order-2'}>
                        <h3 className="text-2xl lg:text-3xl font-bold text-[#e30613] mb-4">
                          {feature.title}
                        </h3>
                        
                        {/* Parse and display description with checkmarks */}
                        {(() => {
                          const description = feature.description;
                          const hasCheckmarks = description.includes('✅');
                          
                          if (hasCheckmarks) {
                            // Split by checkmarks and process each part
                            const parts = description.split('✅').filter(part => part.trim());
                            const textBeforeChecks = parts[0] ? parts[0].trim() : '';
                            const checkItems = parts.slice(1).map(part => part.replace(/^[•\s]+/, '').trim()).filter(item => item);
                            
                            return (
                              <div className="space-y-4">
                                {textBeforeChecks && (
                                  <p className="text-gray-700 text-lg leading-relaxed">
                                    {textBeforeChecks}
                                  </p>
                                )}
                                {checkItems.length > 0 && (
                                  <div className="space-y-3">
                                    {checkItems.map((item, checkIndex) => (
                                      <div key={checkIndex} className="flex items-start space-x-3">
                                        <span className="text-green-500 text-xl flex-shrink-0 mt-0.5">✅</span>
                                        <span className="text-gray-700 text-base leading-relaxed">{item}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          } else {
                            return (
                              <p className="text-gray-700 text-lg leading-relaxed">
                                {description}
                              </p>
                            );
                          }
                        })()}
                      </div>
                      <div className={isEven ? 'order-1 lg:order-2' : 'order-1 lg:order-1'}>
                        <img 
                          src={getImageSrc(categoryImages[imageIndex])}
                          alt={feature.title}
                          className="w-full h-80 object-cover rounded-2xl shadow-lg"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Benefits Section - New Format */}
      {content?.benefits && (
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            {/* Centered Title */}
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#e30613] mb-6">
                {content.benefits.title}
              </h2>
              {content.benefits.description && (
                <p className="text-gray-700 text-lg leading-relaxed max-w-4xl mx-auto">
                  {content.benefits.description}
                </p>
              )}
            </div>

            {/* Benefits List with Alternating Layout */}
            {content.benefits.items.map((benefit, index) => {
              const isEven = index % 2 === 0;
              const categoryImages = imageMapping[categoryId]?.benefits?.items || ['IMG_6844.webp', 'IMG_6845.webp', 'IMG_6846.webp'];
              const imageIndex = index % categoryImages.length;
              
              return (
                <div key={index} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
                  <div className={isEven ? 'order-2 lg:order-1' : 'order-2 lg:order-2'}>
                    <h3 className="text-2xl lg:text-3xl font-bold text-[#e30613] mb-4">
                      {benefit.title}
                    </h3>
                    
                    {/* Parse and display description with checkmarks */}
                    {(() => {
                      const description = benefit.description;
                      const hasCheckmarks = description.includes('✅');
                      
                      if (hasCheckmarks) {
                        // Split by checkmarks and process each part
                        const parts = description.split('✅').filter(part => part.trim());
                        const textBeforeChecks = parts[0] ? parts[0].trim() : '';
                        const checkItems = parts.slice(1).map(part => part.replace(/^[•\s]+/, '').trim()).filter(item => item);
                        
                        return (
                          <div className="space-y-4">
                            {textBeforeChecks && (
                              <p className="text-gray-700 text-lg leading-relaxed">
                                {textBeforeChecks}
                              </p>
                            )}
                            {checkItems.length > 0 && (
                              <div className="space-y-3">
                                {checkItems.map((item, checkIndex) => (
                                  <div key={checkIndex} className="flex items-start space-x-3">
                                    <span className="text-green-500 text-xl flex-shrink-0 mt-0.5">✅</span>
                                    <span className="text-gray-700 text-base leading-relaxed">{item}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        return (
                          <p className="text-gray-700 text-lg leading-relaxed">
                            {description}
                          </p>
                        );
                      }
                    })()}
                  </div>
                  <div className={isEven ? 'order-1 lg:order-2' : 'order-1 lg:order-1'}>
                    <img 
                      src={getImageSrc(categoryImages[imageIndex])}
                      alt={benefit.title}
                      className="w-full h-80 object-cover rounded-2xl shadow-lg"
                    />
                  </div>
                </div>
              );
            })}

            {/* Growth Section for Coworking */}
            {content.benefits.growthSection && (
              <div className="bg-gradient-to-r from-[#e30613] to-[#c1050f] rounded-2xl p-8 md:p-12 mb-12 text-center text-white">
                <h4 className="text-3xl font-bold mb-6">{content.benefits.growthSection.title}</h4>
                <p className="text-xl leading-relaxed max-w-4xl mx-auto opacity-95">
                  {content.benefits.growthSection.description}
                </p>
              </div>
            )}
            
            {/* Additional Features for Coworking */}
            {content.benefits.additionalFeatures && (
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                  Enhance Your Business Potential with Coworking
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {content.benefits.additionalFeatures.map((feature, index) => (
                    <div key={index} className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow text-center">
                      <h5 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h5>
                      <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {products.length > 0 ? (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Available {categoryInfo.title} Options
              </h2>
              <p className="text-gray-600">
                Choose from our range of {categoryInfo.title.toLowerCase()} solutions designed to meet your business needs.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product) => (
                <ProductCard 
                  key={product.id}
                  product={product}
                  category={categoryInfo}
                  className="h-full"
                />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <IconComponent className="w-16 h-16 text-gray-400 mx-auto mb-4" strokeWidth={1} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No products available
            </h3>
            <p className="text-gray-600 mb-6">
              We're currently updating our {categoryInfo.title.toLowerCase()} options. Please check back soon.
            </p>
            <Button onClick={() => navigate('/contact')} className="bg-[#e30613] hover:bg-[#c1050f]">
              Contact Us for Custom Solutions
            </Button>
          </div>
        )}

        {/* Call to Action Section */}
        <div className="mt-16 bg-white rounded-lg border border-gray-200 p-8 text-center">
          <h3 className="text-2xl font-bold text-[#e30613] mb-4">
            Need a Custom {categoryInfo.title} Solution?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Our team can work with you to create a tailored workspace solution that perfectly fits your unique requirements and budget.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-[#e30613] hover:bg-[#c1050f] text-white font-bold px-8"
            >
              Get Custom Quote
            </Button>
            <Button 
              onClick={() => navigate('/about')}
              variant="outline"
              className="border-[#e30613] text-[#e30613] hover:bg-[#e30613] hover:text-white font-bold px-8"
            >
              Learn More About Us
            </Button>
          </div>
        </div>
      </div>

      <Amenities title={`${categoryInfo.title} Amenities`} />

      <Footer />
    </div>
  );
};

export default CategoryPage;